// The menu's screens: home (the tabs: Bhop, Player maps, Map Builder, CS:S maps, Settings), loading (progress,
// errors, picking one map from an archive) and paused (resume / main menu, above the same tabs).
// Settings and keybinds live in the Settings tab and are handled by Menu.

import type { CachedMap } from '../storage/mapCache';
import { mapBoard } from '../online/mapBoard';

export type Screen = 'home' | 'loading' | 'paused';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

const COURSE_TEXT =
  'Hold space to bunnyhop. Strafe in the air with A or D while turning the mouse the same way to gain speed. ' +
  'Hop on the orange blocks as often as you like, but stay on one for more than 0.1 seconds and it drops. ' +
  'The timer starts when you leave the green zone and stops in the red one.';
/** localStorage key of the difficulty filter. */
const TIER_KEY = 'bhop-web-builtin-tier';

const BUILTIN_TEXT =
  'Hop from block to block; stay on one for more than 0.1 seconds and it drops. Every stage has a ' +
  'checkpoint: falling sends you back to the last one you reached. The timer starts when you leave ' +
  'the green zone, each stage gets a split time, and the run ends in the red zone.';
const HOME_TEXT =
  'Source-engine bunnyhop movement at 100 tick. Play the built-in maps, build your own, or play community ' +
  'bhop maps from GameBanana.';

function fmtSize(bytes: number) {
  return bytes >= 1048576
    ? (bytes / 1048576).toFixed(1) + ' MB'
    : Math.max(1, Math.round(bytes / 1024)) + ' KB';
}

function fmtDate(ms: number) {
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export class MainMenu {
  screen: Screen = 'home';
  onPlayCourse: () => void = () => {};
  onPlayBuiltin: (name: string) => void = () => {};
  /** Show a map's hiscores (board key, title). */
  onScores: (board: string, title: string) => void = () => {};
  onPickFile: (file: File) => void = () => {};
  onPlayCached: (map: CachedMap) => void = () => {};
  onDeleteCached: (map: CachedMap) => void = () => {};
  /** Back from the loading screen (cancels a load in progress). */
  onBack: () => void = () => {};
  /** Main menu from the paused screen. */
  onHome: () => void = () => {};

  private readonly fileInput = $<HTMLInputElement>('mapFile');
  private pickResolve: ((name: string | null) => void) | null = null;

  constructor() {
    $('playCourse').addEventListener('click', () => this.onPlayCourse());
    $('loadMap').addEventListener('click', () => {
      this.fileInput.value = '';
      this.fileInput.click();
    });
    this.fileInput.addEventListener('change', () => {
      const f = this.fileInput.files?.[0];
      if (f) this.onPickFile(f);
    });
    $('loadBack').addEventListener('click', () => {
      this.resolvePick(null);
      this.onBack();
    });
    $('toMenu').addEventListener('click', () => this.onHome());
    this.show('home');
  }

  show(screen: Screen) {
    this.screen = screen;
    // The tabs show on the home screen and under the paused screen's buttons.
    $('home').hidden = screen === 'loading';
    $('loading').hidden = screen !== 'loading';
    $('paused').hidden = screen !== 'paused';
    if (screen === 'home') this.setHeader('CSS-BHOP', HOME_TEXT);
  }

  /** Header and note for the paused screen of the current level. */
  setPaused(kind: 'course' | 'map' | 'builtin' | 'custom', name: string, note = '') {
    this.setHeader(
      name,
      kind === 'course'
        ? COURSE_TEXT
        : kind === 'builtin' || kind === 'custom'
          ? BUILTIN_TEXT
          : 'Your own map. The timer uses the zones below (standard ones load by themselves when ' +
            'there are any); press R to go back to the spawn point.',
    );
    $('mapNote').textContent = note;
    $('zonePanel').hidden = kind !== 'map';
    this.show('paused');
  }

  private setHeader(title: string, text: string) {
    $('menuTitle').textContent = title;
    $('menuText').textContent = text;
  }

  startLoading(name: string) {
    this.setHeader('Loading map', 'Reading the file on your computer. Nothing is uploaded.');
    $('loadName').textContent = name;
    $('loadError').hidden = true;
    $('pickList').hidden = true;
    $('loadBack').textContent = 'Cancel';
    this.progress('Reading file', 0);
    this.show('loading');
  }

  progress(stage: string, fraction: number) {
    $('loadStage').textContent = stage;
    $('loadBar').style.width = Math.round(Math.max(0, Math.min(1, fraction)) * 100) + '%';
  }

  error(message: string) {
    this.resolvePick(null);
    $('pickList').hidden = true;
    const e = $('loadError');
    e.textContent = message;
    e.hidden = false;
    $('loadStage').textContent = "Couldn't load this map.";
    $('loadBack').textContent = 'Back';
    this.show('loading');
  }

  /** Ask which map to load from an archive that has several. */
  pick(names: string[]): Promise<string | null> {
    this.resolvePick(null);
    const list = $('pickList');
    list.textContent = '';
    $('loadStage').textContent = 'This file has several maps. Pick one:';
    return new Promise((resolve) => {
      this.pickResolve = resolve;
      for (const n of names) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'secondary';
        b.textContent = n;
        b.addEventListener('click', () => {
          list.hidden = true;
          this.resolvePick(n);
        });
        list.append(b);
      }
      list.hidden = false;
    });
  }

  private resolvePick(name: string | null) {
    const r = this.pickResolve;
    this.pickResolve = null;
    r?.(name);
  }

  /** Difficulty shown in the built-in maps list (0 = all), remembered in this browser. */
  private tier = (() => {
    try {
      return Number(localStorage.getItem(TIER_KEY)) || 0;
    } catch {
      return 0;
    }
  })();

  /** The difficulty filter over the built-in maps: all, or one to five stars. */
  private tierBar() {
    const bar = $('builtinTiers');
    if (bar.childElementCount) return;
    for (let t = 0; t <= 5; t++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = t ? '★'.repeat(t) : 'All';
      b.title = t ? `Difficulty ${t} of 5` : 'Every map';
      b.classList.toggle('on', t === this.tier);
      b.addEventListener('click', () => {
        this.tier = t;
        try {
          localStorage.setItem(TIER_KEY, String(t));
        } catch {
          // Not remembered: fine.
        }
        for (const x of bar.children) x.classList.toggle('on', x === b);
        for (const li of $('builtinList').children as HTMLCollectionOf<HTMLElement>)
          li.hidden = t !== 0 && li.dataset.tier !== String(t);
      });
      bar.append(b);
    }
  }

  /** null = the browser can't store maps. */
  /** The built-in maps with their colours and personal bests. */
  setBuiltins(
    maps: {
      name: string;
      title: string;
      difficulty: number;
      stages: number;
      color: string;
      pb: string | null;
    }[],
  ) {
    const list = $('builtinList');
    list.textContent = '';
    this.tierBar();
    for (const m of maps) {
      const li = document.createElement('li');
      li.dataset.map = m.name;
      li.dataset.tier = String(m.difficulty);
      li.hidden = this.tier !== 0 && m.difficulty !== this.tier;
      const b = document.createElement('button');
      b.type = 'button';
      b.style.background = `linear-gradient(135deg, ${m.color}55, rgba(0,0,0,0.25))`;
      const t = document.createElement('b');
      t.textContent = m.title;
      const stars = document.createElement('span');
      stars.className = 'stars';
      stars.textContent = '★'.repeat(m.difficulty) + '☆'.repeat(5 - m.difficulty);
      stars.title = `Difficulty ${m.difficulty} of 5`;
      const stages = document.createElement('span');
      stages.className = 'stagesInfo';
      stages.textContent = m.stages === 1 ? '1 stage' : `${m.stages} stages`;
      const pb = document.createElement('span');
      pb.className = 'pb';
      pb.textContent = m.pb ? 'PB ' + m.pb : 'not finished yet';
      b.append(t, stars, stages, pb);
      b.addEventListener('click', () => this.onPlayBuiltin(m.name));
      const top = document.createElement('button');
      top.type = 'button';
      top.className = 'trophy';
      top.textContent = '🏆';
      top.title = `Hiscores of ${m.title}`;
      top.setAttribute('aria-label', top.title);
      top.addEventListener('click', () => this.onScores('b:' + m.name, m.title));
      li.append(b, top);
      list.append(li);
    }
  }

  setMaps(maps: CachedMap[] | null) {
    const box = $('yourMaps');
    const list = $('mapList');
    list.textContent = '';
    if (maps === null) {
      box.hidden = false;
      const li = document.createElement('li');
      li.textContent =
        "This browser can't keep maps (private window or storage blocked). Load the file each time.";
      list.append(li);
      return;
    }
    box.hidden = maps.length === 0;
    for (const m of maps) {
      const li = document.createElement('li');
      const info = document.createElement('div');
      const name = document.createElement('div');
      name.className = 'mapName';
      name.textContent = m.name;
      name.title = m.sourceName;
      const meta = document.createElement('span');
      meta.className = 'mapMeta';
      meta.textContent = `${fmtSize(m.size)} · last played ${fmtDate(m.lastPlayed)}`;
      info.append(name, meta);
      const play = document.createElement('button');
      play.type = 'button';
      play.className = 'playMap';
      play.textContent = 'Play';
      play.addEventListener('click', () => this.onPlayCached(m));
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'del';
      del.textContent = 'Delete';
      del.setAttribute('aria-label', `Delete ${m.name} from this browser`);
      del.addEventListener('click', () => {
        // Two clicks, so a stray click doesn't remove a map.
        if (!del.classList.contains('confirm')) {
          del.classList.add('confirm');
          del.textContent = 'Sure?';
          setTimeout(() => {
            del.classList.remove('confirm');
            del.textContent = 'Delete';
          }, 3000);
          return;
        }
        this.onDeleteCached(m);
      });
      const top = document.createElement('button');
      top.type = 'button';
      top.className = 'trophy';
      top.textContent = '🏆';
      top.title = `Hiscores of ${m.name} (standard zones)`;
      top.setAttribute('aria-label', top.title);
      top.addEventListener('click', () => this.onScores(mapBoard(m.name), m.name));
      li.append(info, play, top, del);
      list.append(li);
    }
  }
}
